import type { CSSProperties } from 'react';
import { DEFAULT_AVATAR } from '@tg/shared';
import { Avatar } from '@/ui';
import { AvatarArt } from '@/art/AvatarArt';
import { AVATAR_ART_IDS, isAvatarArt } from '@/art/avatars';
import { ZODIAC_IDS } from '@/art/zodiac';
import { useNickname } from '@/i18n/useNickname';
import s from './profile.module.css';

/**
 * Preset avatars (server stores only the id, zAvatar). `initial` = first letter of the nickname (design default,
 * also a user choice); a01…a09 = geometric animal / robot faces (art/avatars.ts), round and circle-safe.
 */
export const AVATAR_IDS: string[] = [DEFAULT_AVATAR, ...AVATAR_ART_IDS];

/** Zodiac presets z01…z12 (art/zodiac.ts) — a separate group in the avatar picker. */
export const ZODIAC_AVATAR_IDS: string[] = [...ZODIAC_IDS];

/** Avatar by preset id; `initial` and unknown ids show the nickname initial. */
export function AvatarPreset({ id, name, size = 34, className }: { id: string; name: string; size?: number; className?: string }) {
  if (!isAvatarArt(id)) return <Avatar name={name} size={size} className={className} />;
  const st: CSSProperties = { width: size, height: size, borderWidth: 0 };
  return (
    <span aria-hidden="true" className={[s.preset, className].filter(Boolean).join(' ')} style={st}>
      <AvatarArt id={id} size="100%" />
    </span>
  );
}

/** Small avatar + name for player tables (crash / roulette): preset art, or the initial for `initial`. */
export function PlayerName({ avatar, name, label, size = 18 }: { avatar: string; name: string; label?: string; size?: number }) {
  const nick = useNickname();
  return (
    <span className={s.playerName}>
      <AvatarPreset id={avatar} name={name} size={size} className={s.playerAv} />
      <span className={s.playerNameText}>{label ?? nick(name)}</span>
    </span>
  );
}
